import { FileText, Loader2, Upload, X } from "lucide-react";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { ragService } from "../../services/rag";
import { Button } from "../ui/button";

interface DocumentUploadProps {
	routeConfigId: string;
	onUploadComplete: () => void;
	maxFileSize?: number; // in bytes
	acceptedTypes?: string[];
}

export function DocumentUpload({
	routeConfigId,
	onUploadComplete,
	maxFileSize = 50 * 1024 * 1024, // 50MB default
	acceptedTypes = ["application/pdf", "text/plain", "text/markdown"],
}: DocumentUploadProps) {
	const [isDragging, setIsDragging] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

	const validateFile = (file: File): string | null => {
		if (!acceptedTypes.includes(file.type)) {
			return `File type ${file.type} is not supported. Supported types: PDF, TXT, MD`;
		}
		if (file.size > maxFileSize) {
			return `File size ${(file.size / 1024 / 1024).toFixed(2)}MB exceeds maximum of ${(maxFileSize / 1024 / 1024).toFixed(2)}MB`;
		}
		return null;
	};

	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	}, []);

	const handleDragLeave = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
	}, []);

	const handleDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			setIsDragging(false);

			const files = Array.from(e.dataTransfer.files);
			const validFiles: File[] = [];
			const errors: string[] = [];

			files.forEach((file) => {
				const error = validateFile(file);
				if (error) {
					errors.push(`${file.name}: ${error}`);
				} else {
					validFiles.push(file);
				}
			});

			if (errors.length > 0) {
				toast.error(errors.join("\n"));
			}

			if (validFiles.length > 0) {
				setSelectedFiles((prev) => [...prev, ...validFiles]);
			}
		},
		[validateFile],
	);

	const handleFileSelect = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const files = Array.from(e.target.files || []);
			const validFiles: File[] = [];
			const errors: string[] = [];

			files.forEach((file) => {
				const error = validateFile(file);
				if (error) {
					errors.push(`${file.name}: ${error}`);
				} else {
					validFiles.push(file);
				}
			});

			if (errors.length > 0) {
				toast.error(errors.join("\n"));
			}

			if (validFiles.length > 0) {
				setSelectedFiles((prev) => [...prev, ...validFiles]);
			}
		},
		[validateFile],
	);

	const handleUpload = useCallback(async () => {
		if (selectedFiles.length === 0) return;

		setUploading(true);
		setUploadProgress(0);

		try {
			for (let i = 0; i < selectedFiles.length; i++) {
				const file = selectedFiles[i];
				await ragService.uploadDocument(routeConfigId, file, (progress) => {
					setUploadProgress(
						(i / selectedFiles.length) * 100 + progress / selectedFiles.length,
					);
				});
				toast.success(`Uploaded ${file.name}`);
			}

			setSelectedFiles([]);
			onUploadComplete();
		} catch (err) {
			let message = "Failed to upload document";
			if (err instanceof Error) {
				message = err.message;
				// Check if it's the RAG not enabled error
				if (
					err.message.includes("RAG is not enabled") ||
					err.message.includes("400")
				) {
					message =
						"RAG is not enabled for this RouteConfig. Please enable RAG in the RouteConfig settings first.";
				}
			}
			toast.error(message);
		} finally {
			setUploading(false);
			setUploadProgress(0);
		}
	}, [selectedFiles, routeConfigId, onUploadComplete]);

	const removeFile = useCallback((index: number) => {
		setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
	}, []);

	return (
		<div className="space-y-4">
			<div
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
				className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
					isDragging
						? "border-primary bg-primary/5"
						: "border-muted-foreground/25 hover:border-muted-foreground/50"
				}`}
			>
				<Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
				<p className="text-sm font-medium mb-2">
					Drag and drop files here, or click to select
				</p>
				<p className="text-xs text-muted-foreground mb-4">
					PDF, TXT, or Markdown files up to{" "}
					{(maxFileSize / 1024 / 1024).toFixed(0)}MB
				</p>
				<input
					type="file"
					id="file-upload"
					className="hidden"
					multiple
					accept={acceptedTypes.join(",")}
					onChange={handleFileSelect}
					disabled={uploading}
				/>
				<label htmlFor="file-upload">
					<Button variant="outline" asChild disabled={uploading}>
						<span>Select Files</span>
					</Button>
				</label>
			</div>

			{selectedFiles.length > 0 && (
				<div className="space-y-2">
					<div className="flex items-center justify-between">
						<p className="text-sm font-medium">
							{selectedFiles.length} file(s) selected
						</p>
						<Button onClick={handleUpload} disabled={uploading}>
							{uploading ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Uploading...
								</>
							) : (
								<>
									<Upload className="mr-2 h-4 w-4" />
									Upload {selectedFiles.length} file(s)
								</>
							)}
						</Button>
					</div>

					{uploading && (
						<div className="w-full bg-secondary rounded-full h-2">
							<div
								className="bg-primary h-2 rounded-full transition-all"
								style={{ width: `${uploadProgress}%` }}
							/>
						</div>
					)}

					<div className="space-y-2">
						{selectedFiles.map((file, index) => (
							<div
								key={index}
								className="flex items-center justify-between p-3 border rounded-lg"
							>
								<div className="flex items-center gap-2">
									<FileText className="h-4 w-4 text-muted-foreground" />
									<span className="text-sm">{file.name}</span>
									<span className="text-xs text-muted-foreground">
										({(file.size / 1024 / 1024).toFixed(2)} MB)
									</span>
								</div>
								{!uploading && (
									<Button
										variant="ghost"
										size="icon"
										onClick={() => removeFile(index)}
									>
										<X className="h-4 w-4" />
									</Button>
								)}
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
}
